<template>
  <div class="body dg" id="manage-payments-container">
    <h2 class="manage-payments-title">Manage Payments</h2>
    <div class="manage-payments-container">
      <div class="manage-payments-card">
        <h2 class="card-title">Select your Stripe account</h2>
        <p class="card-description">
          Please select a Stripe Account or add a new one. Rallyhood integrates with Stripe for safe, secure PCI Level 1 compliant money collection. Please follow the instructions for connecting the account to this Rally. For more information, please click on the info link provided.
        </p>

        <h3 class="section-title">Connected Stripe Accounts</h3>

        <div v-if="loading" class="loading-state">
          <i class="fas fa-spinner fa-spin"></i>
        </div>

        <div v-else class="accounts-list">
          <div
            v-for="(acc, index) in rallyAccounts"
            :key="acc.id || index"
            class="stripe-account-row"
          >
            <div
              class="account-title"
              :class="{ 'is-disabled': isAccountDisabled(acc) }"
            >
              {{ getAccountTitle(acc) }}
            </div>

            <div class="account-actions">
              <template v-if="isAccountComplete(acc) && !isAccountDisabled(acc)">
                <a
                  href="#"
                  class="login-stripe-link"
                  @click.prevent="openLoginUrl(acc)"
                >
                  {{ getStripeLinkText(acc) }}
                </a>
                <button
                  type="button"
                  class="btn-download-reports"
                  @click="openDownloadModal(acc)"
                >
                  Download Reports
                </button>
              </template>

              <template v-else>
                <button
                  type="button"
                  class="btn-continue-enrollment"
                  @click="continueEnrollment(acc)"
                >
                  Continue Enrollment
                </button>
              </template>
            </div>
          </div>

          <div v-if="!rallyAccounts || rallyAccounts.length === 0" class="empty-accounts">
            <p>No connected Stripe accounts yet.</p>
          </div>
        </div>

        <div class="create-stripe-section">
          <p class="create-stripe-prompt">
            Sign up to quickly create a new Stripe account and get started accepting payments.
          </p>
          <button
            type="button"
            class="btn-create-stripe"
            @click="toggleModal"
          >
            <span>Create a</span>
            <img src="/stripe-small-white.svg" alt="stripe" />
            <span>Account</span>
          </button>
        </div>

        <div class="note-section">
          <p><strong>Note:</strong> If your event has paid tickets, your attendees will not be able to register until your Stripe account has been connected.</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapState } from "vuex";
import { userInfo } from "@/common/user-info";
import { Capacitor } from "@capacitor/core";
import { Browser } from "@capacitor/browser";

export default {
  name: "ManagePayments",
  props: ['rallyId'],
  data: function() {
    return {
      rallyAccountsLoaded: false,
      loading: true,
      isRunningInCapacitor: false
    };
  },
  computed: {
    ...mapState("rfins", ["accountsByRallyId"]),
    ...mapState("rallies", ["allRalliesByRallyId"]),
    isAdmin() {
      return (this.myRole === "Admin" || this.myRole === "Owner");
    },
    selectedRally() {
      return this.allRalliesByRallyId[this.rallyId];
    },
    myRole() {
      return this.selectedRally && this.selectedRally.membership
        ? this.selectedRally.membership.attributes["role"]
        : "Friend";
    },
    rallyAccounts() {
      if (this.rallyAccountsLoaded && this.accountsByRallyId[parseInt(this.$route.params.rallyId)]) {
        return this.accountsByRallyId[parseInt(this.$route.params.rallyId)].filter((acc) => {
          return (
            acc.attributes['account-type'] !== 'default' &&
            (this.isAdmin || (acc.attributes && acc.attributes["creator-id"] == userInfo().user_id))
          );
        });
      }
      return [];
    }
  },
  mounted() {
    this.isRunningInCapacitor = Capacitor.getPlatform() === "android" || Capacitor.getPlatform() === "ios";
    this.loadData();
  },
  methods: {
    ...mapActions("rfins", ["fetchAccounts"]),
    ...mapActions("user", ["setModalProps"]),
    async loadData() {
      try {
        await this.fetchAccounts({ rallyId: parseInt(this.$route.params.rallyId), returnUrl: window.location.href });
      } catch (e) {
        console.error(e);
      }
      this.rallyAccountsLoaded = true;
      this.loading = false;
    },
    isAccountComplete(acc) {
      return acc.attributes && acc.attributes['stripe-status'] === 'enabled';
    },
    isAccountDisabled(acc) {
      return Boolean(acc.attributes && acc.attributes['disabled']);
    },
    isExpress(acc) {
      return acc.attributes && acc.attributes['account-type'] === 'stripe';
    },
    getAccountTitle(acc) {
      if (!acc || !acc.attributes) return "Bank Account";
      let title = acc.attributes['title'] || "Bank Account";
      if (this.isExpress(acc)) {
        title = "(E) - " + title;
      }
      if (this.isAccountDisabled(acc)) {
        title = title + " (Disabled)";
      }
      return title;
    },
    getStripeLinkText(acc) {
      if (this.isExpress(acc)) {
        return "Open Stripe Dashboard";
      }
      return "Login to Stripe";
    },
    openLoginUrl(acc) {
      let url = "https://dashboard.stripe.com/login";
      if (this.isExpress(acc) && acc.attributes && acc.attributes['connect-dashboard-link']) {
        url = acc.attributes['connect-dashboard-link']['url'] || url;
      }

      if (this.isRunningInCapacitor) {
        Browser.open({ url });
      } else {
        window.open(url, "_blank");
      }
    },
    continueEnrollment(acc) {
      const url = acc.attributes ? acc.attributes['stripe-standard-enrollment-url'] : null;
      if (!url) return;
      if (this.isRunningInCapacitor) {
        Browser.open({ url });
      } else {
        window.location.href = url;
      }
    },
    openDownloadModal(acc) {
      this.setModalProps({
        name: "DownloadReportsModal",
        account: acc,
        rallyId: parseInt(this.rallyId || this.$route.params.rallyId)
      });
    },
    toggleModal() {
      this.setModalProps({
        name: "StripeSetupModal",
        // The modal's create/import actions post rally_id from this prop;
        // without it rfins rejects the request (and the modal still reports
        // success). Same fallback as openDownloadModal above.
        rallyId: parseInt(this.rallyId || this.$route.params.rallyId)
      });
    }
  }
};
</script>
